<template>
  <div class="book-old-books-page min-vh-100 d-flex flex-column">
    <Header />

    <main class="main-content flex-grow-1 py-4">
      <div class="container">

        <!-- Top Header & Book Showcase Banner -->
        <div class="bob-header-card p-4 rounded-4 shadow-sm mb-4">
          <!-- Top Bar Navigation -->
          <div class="bob-top-bar d-flex justify-content-between align-items-center mb-3">
            <button type="button" @click="$router.push('/book/' + idBook)" class="btn-back-link">
              <icon icon="fa-solid fa-arrow-left" class="me-1" />
              <span>Quay lại chi tiết sách</span>
            </button>

            <router-link :to="'/old-books/create?idBook=' + idBook" class="btn-post-new-sm">
              <icon icon="fa-solid fa-plus" class="me-1" />
              <span>Đăng bán sách này</span>
            </router-link>
          </div>

          <!-- Book Showcase Info -->
          <div class="bob-showcase d-flex align-items-center gap-4">
            <!-- Book Cover Frame -->
            <div class="catalog-book-cover-frame">
              <img 
                v-if="catalogBook && catalogBook.cover && !catalogCoverError" 
                :src="catalogBook.cover" 
                :alt="catalogBook.name" 
                class="catalog-cover-img"
                referrerpolicy="no-referrer" 
                @error="catalogCoverError = true" 
              />
              <!-- Branded Fallback Cover when cover image is unavailable or broken -->
              <div v-else class="catalog-cover-fallback">
                <icon icon="fa-solid fa-book-open" class="fallback-icon mb-2" />
                <span class="fallback-title">{{ (catalogBook && catalogBook.name) || 'Lối Đọc' }}</span>
              </div>
            </div>

            <!-- Book Meta & Title -->
            <div class="bob-book-info flex-grow-1">
              <div class="bob-pill-tag mb-1">
                <icon icon="fa-solid fa-layer-group" class="me-1" />
                <span>Sàn sách cũ • Theo đầu sách</span>
              </div>

              <h1 class="bob-title font-serif fw-bold m-0" :title="catalogBook ? catalogBook.name : ''">
                {{ catalogBook ? catalogBook.name : 'Gian hàng sách cũ' }}
              </h1>

              <div class="bob-meta-tags mt-2 d-flex flex-wrap align-items-center gap-3" v-if="catalogBook">
                <span v-if="catalogAuthorsStr" class="bob-meta-item">
                  <icon icon="fa-solid fa-feather-pointed" class="me-1 text-emerald-600" />
                  <span>{{ catalogAuthorsStr }}</span>
                </span>
                <span v-if="catalogCategoriesStr" class="bob-meta-item">
                  <icon icon="fa-solid fa-tags" class="me-1 text-emerald-600" />
                  <span>{{ catalogCategoriesStr }}</span>
                </span>
              </div>

              <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                <span class="bob-count-badge">
                  <icon icon="fa-solid fa-store" />
                  <span><strong>{{ totalElements }}</strong> gian hàng đang bán</span>
                </span>
                <router-link :to="'/book/' + idBook" class="bob-view-detail-link">
                  <span>Xem thông tin &amp; nhận xét gốc</span>
                  <icon icon="fa-solid fa-arrow-up-right-from-square" class="ms-1" />
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="shopee-loading-section text-center py-5">
          <div class="spinner-border text-emerald-600 mb-2" role="status">
            <span class="visually-hidden">Đang tải...</span>
          </div>
          <p class="text-muted fs-7">Đang tải danh sách gian hàng sách cũ...</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="booths.length === 0" class="bob-empty-card text-center py-5 p-4 rounded-4 border">
          <div class="empty-icon-circle mx-auto mb-3">
            <icon icon="fa-solid fa-box-open" />
          </div>
          <h3 class="font-serif fw-bold text-slate-800 mb-2">Chưa có gian hàng nào đăng bán cuốn sách này</h3>
          <p class="text-muted fs-7 max-w-500 mx-auto mb-4">
            Hiện tại chưa có thành viên nào sang nhượng cuốn <strong>{{ catalogBook ? catalogBook.name : 'này' }}</strong>. 
            Nếu bạn có sẵn cuốn sách này ở nhà, hãy đăng bán để chia sẻ tới những độc giả đang tìm kiếm nhé!
          </p>
          <router-link :to="'/old-books/create?idBook=' + idBook" class="btn-post-new-sm d-inline-flex mx-auto">
            <icon icon="fa-solid fa-plus" class="me-2" />
            <span>Trở thành người đầu tiên đăng bán</span>
          </router-link>
        </div>

        <!-- Booths Listing -->
        <div v-else>
          <!-- Toolbar (Sort & Total) -->
          <div class="bob-toolbar mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div class="toolbar-left d-flex align-items-center gap-2">
              <span class="toolbar-label fw-bold text-slate-800">
                Danh sách bài đăng sang nhượng ({{ totalElements }})
              </span>
            </div>
            <div class="toolbar-right d-flex align-items-center gap-2">
              <span class="fs-8 text-muted">Sắp xếp:</span>
              <select v-model="selectedSort" class="form-select form-select-sm sort-select">
                <option value="default">Mới nhất</option>
                <option value="price_asc">Giá: Thấp đến cao ↑</option>
                <option value="price_desc">Giá: Cao đến thấp ↓</option>
              </select>
            </div>
          </div>

          <!-- Booths Grid -->
          <div class="shopee-grid">
            <div 
              v-for="booth in sortedBooths" 
              :key="booth.id" 
              class="shopee-card"
              @click="goToDetail(booth.id)"
            >
              <!-- Cover Image Frame -->
              <div class="shopee-card-img">
                <img 
                  :src="getCoverUrl(booth.coverImage || (booth.books && booth.books[0] && booth.books[0].cover))" 
                  :alt="booth.name" 
                  class="shopee-img"
                  loading="lazy"
                  @error="handleImageError"
                />

                <!-- Top Badges -->
                <div class="shopee-badge-stack">
                  <span class="shopee-cond-tag" :class="{ 'is-combo': booth.totalBooks > 1 }">
                    <icon :icon="booth.totalBooks > 1 ? 'fa-solid fa-layer-group' : 'fa-solid fa-book'" class="me-1" />
                    {{ booth.totalBooks > 1 ? ('Combo ' + booth.totalBooks + ' cuốn') : '1 cuốn' }}
                  </span>
                  <span class="shopee-sale-type-tag" :class="booth.saleType === 1 ? 'type-combo' : 'type-single'">
                    {{ booth.saleType === 1 ? 'Mua combo' : 'Bán lẻ' }}
                  </span>
                </div>

                <!-- Discount Tag -->
                <div v-if="booth.discountRate && booth.discountRate > 0" class="my-booth-sale-tag">
                  -{{ booth.discountRate }}%
                </div>

                <!-- Hover Quick View Button -->
                <button class="shopee-btn-quick" @click.stop="goToDetail(booth.id)" title="Xem bài đăng">
                  <icon icon="fa-solid fa-eye" class="me-1" /> Xem gian hàng
                </button>
              </div>

              <!-- Card Body -->
              <div class="shopee-card-body">
                <h3 class="shopee-card-title font-serif" :title="booth.name">
                  {{ booth.name }}
                </h3>

                <!-- Authors -->
                <div class="shopee-card-meta-row" v-if="getAuthorsList(booth).length > 0">
                  <span class="meta-item-author">
                    <icon icon="fa-solid fa-feather-pointed" class="me-1" />
                    {{ getAuthorsList(booth).slice(0, 2).join(', ') }}
                  </span>
                </div>

                <!-- Description or Combo Preview -->
                <p class="shopee-card-desc" v-if="booth.books && booth.books.length > 1">
                  <strong class="text-emerald-700">Bộ gồm:</strong> {{ booth.books.map(b => b.name).join(' • ') }}
                </p>
                <p class="shopee-card-desc" v-else-if="booth.description">
                  {{ booth.description }}
                </p>
                <p class="shopee-card-desc text-muted fst-italic" v-else>
                  Sách cũ được giữ gìn cẩn thận
                </p>

                <!-- Price Row -->
                <div class="shopee-card-price-row mt-auto">
                  <div class="d-flex align-items-baseline gap-2 flex-wrap">
                    <span class="shopee-price">{{ formatPrice(booth.price) }}</span>
                    <span v-if="booth.originalPrice && booth.originalPrice > booth.price" class="shopee-original-price">
                      {{ formatPrice(booth.originalPrice) }}
                    </span>
                  </div>
                </div>

                <!-- Footer: Seller & Status -->
                <div class="shopee-public-footer mt-2 pt-2 border-top">
                  <span class="public-seller-name" :title="'Người bán: ' + (booth.createByName || 'Người dùng')">
                    <icon icon="fa-solid fa-store" class="me-1 text-emerald-600" />
                    {{ booth.createByName || 'Người bán' }}
                  </span>
                  <span class="public-status-tag" :class="'status-tag-' + booth.status">
                    <span class="status-dot"></span>
                    {{ getListingStatusLabel(booth.status) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="totalPages > 1" class="shopee-pagination mt-4">
            <ul class="pagination-list">
              <li :class="{ disabled: currentPage === 0 }">
                <button class="page-btn" @click="fetchBooths(currentPage - 1)"><icon icon="fa-solid fa-chevron-left" /></button>
              </li>
              <li v-for="p in totalPages" :key="p" :class="{ active: (p - 1) === currentPage }">
                <button class="page-btn" @click="fetchBooths(p - 1)">{{ p }}</button>
              </li>
              <li :class="{ disabled: currentPage >= totalPages - 1 }">
                <button class="page-btn" @click="fetchBooths(currentPage + 1)"><icon icon="fa-solid fa-chevron-right" /></button>
              </li>
            </ul>
          </div>
        </div>

      </div>
    </main>

    <Footer />
  </div>
</template>

<script src="./book-old-books.js"></script>
<style scoped src="./book-old-books.css"></style>
